import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";

import { inbox } from "./inbox.graphql";
import {
  countForCategory,
  inboxBadgeTotal,
  type InboxCategory,
  type InboxCategoryCount,
} from "./kinds";
import {
  inboxQueryVariables,
  inboxRawPage,
  INBOX_PAGE_SIZE,
  type InboxQueryVariables,
} from "./query";
import type { InboxRoute } from "./InboxHeader";
import { parseInboxRow, type InboxItem } from "./registry";
import type { InboxRow } from "./row";

type InboxQuery = {
  inbox?: {
    total?: number | null;
    counts?: InboxCategoryCount[] | null;
    routes?: InboxRoute[] | null;
    items?: InboxRow[] | null;
  } | null;
};

export { INBOX_PAGE_SIZE };

/** Hold previous while in flight; a completed empty array is real. */
const holdInboxFacet = <T>(
  current: T[] | null | undefined,
  previous: T[] | null | undefined,
  inFlight: boolean
): T[] =>
  inFlight
    ? current && current.length > 0
      ? current
      : (previous ?? [])
    : (current ?? previous ?? []);

const inboxListVariables = ({
  category,
  routeId,
  teamId,
  pageSize,
  after,
}: {
  category: InboxCategory;
  routeId: string | null;
  teamId: string | null;
  pageSize: number;
  after?: string;
}) =>
  inboxQueryVariables({
    category,
    routeId,
    teamId,
    limit: pageSize,
    ...(after ? { after } : {}),
  });

export const useInboxItems = (
  enabled: boolean,
  options?: {
    category?: InboxCategory;
    routeId?: string | null;
    teamId?: string | null;
    pageSize?: number;
  }
): {
  items: InboxItem[];
  total: number;
  counts: InboxCategoryCount[];
  waitingCount: number;
  routes: InboxRoute[];
  routesLoaded: boolean;
  loading: boolean;
  error: unknown;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
  categoryPending: boolean;
} => {
  const {
    category = "EVERYTHING",
    routeId = null,
    teamId = null,
    pageSize = INBOX_PAGE_SIZE,
  } = options ?? {};
  const variables = inboxListVariables({
    category,
    routeId,
    teamId,
    pageSize,
  });
  const { data, previousData, loading, error, fetchMore } = useQuery<
    InboxQuery,
    InboxQueryVariables
  >(inbox, {
    skip: !enabled,
    variables,
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const counts = holdInboxFacet(
    data?.inbox?.counts,
    previousData?.inbox?.counts,
    loading
  );
  const routes = holdInboxFacet(
    data?.inbox?.routes,
    previousData?.inbox?.routes,
    loading
  );
  const itemInbox = data?.inbox ?? (!loading ? previousData?.inbox : undefined);
  const items = useMemo(
    () =>
      (itemInbox?.items ?? []).flatMap((row) => {
        const item = parseInboxRow(row);
        return item ? [item] : [];
      }),
    [itemInbox]
  );
  const total = countForCategory(counts, category);
  const waitingCount = inboxBadgeTotal(counts);
  const wireTotal = itemInbox?.total ?? total;
  const { hasMore, after } = itemInbox
    ? inboxRawPage(itemInbox.items ?? [], wireTotal, pageSize)
    : { hasMore: total > 0, after: undefined };
  const loadMore = () => {
    if (!hasMore || !after || loadingMore) {
      return;
    }
    setLoadingMore(true);
    void fetchMore({
      variables: inboxListVariables({
        category,
        routeId,
        teamId,
        pageSize,
        after,
      }),
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult?.inbox) {
          return previous;
        }
        const counts =
          fetchMoreResult.inbox.counts ?? previous.inbox?.counts ?? null;
        const routes =
          fetchMoreResult.inbox.routes ?? previous.inbox?.routes ?? null;
        return {
          inbox: {
            ...fetchMoreResult.inbox,
            items: [
              ...(previous.inbox?.items ?? []),
              ...(fetchMoreResult.inbox.items ?? []),
            ],
            counts,
            routes,
          },
        };
      },
    }).finally(() => {
      setLoadingMore(false);
    });
  };
  return {
    items,
    total,
    counts,
    waitingCount,
    routes,
    routesLoaded: data?.inbox != null || previousData?.inbox != null,
    loading: loading && data?.inbox == null && previousData?.inbox == null,
    error,
    hasMore,
    loadingMore,
    loadMore,
    categoryPending: itemInbox == null && counts.length > 0,
  };
};
